import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth token handling.
//
// The JWT is delivered by every login flow BOTH as an httpOnly cookie (set by
// the backend) AND in the response body. Historically we only kept the body
// token in memory (XSS-safe) and relied on the cookie to restore the session
// after a reload. That breaks in two real-world topologies:
//   1. Production where the frontend origin differs from the API origin — the
//      cookie becomes cross-site and (with SameSite=Lax) is NOT sent on the
//      /auth/me bootstrap → forced logout on every refresh.
//   2. Inside the iOS Capacitor WKWebView, where WebKit blocks cross-site /
//      third-party cookies outright.
// In both cases users were "logged out after a while" (i.e. on any reload / SW
// update / app reopen). To keep users reliably signed in across ALL topologies
// we persist the Bearer token in localStorage and re-attach it on boot. The
// backend `get_current_user` accepts `Authorization: Bearer <jwt>`, so this
// works even when the cookie is unavailable. (The token is sent only as an
// explicit header — never auto-attached to cross-site form posts — so this
// does not widen the CSRF surface the way a SameSite=None cookie would.)
const AUTH_STORE_KEY = "rg_auth";
let _memToken = null;
try { _memToken = localStorage.getItem(AUTH_STORE_KEY) || null; } catch { _memToken = null; }
export function setAuthToken(t) {
  _memToken = t || null;
  try {
    if (t) localStorage.setItem(AUTH_STORE_KEY, t);
    else localStorage.removeItem(AUTH_STORE_KEY);
  } catch { /* private mode / quota — memory token still works for this session */ }
}
export function getAuthToken() { return _memToken; }

// مهلة افتراضية لكل الطلبات — طلب معلّق لا يجمّد الواجهة للأبد.
// 60s للطلبات العادية (يشمل توليد AI البطيء)، 5 دقائق لرفع الملفات (FormData).
const http = axios.create({ baseURL: API, withCredentials: true, timeout: 60000 });

// ⚡ كاش SWR لطلبات الكتالوج العامة — فتح الصفحات يصبح فورياً:
// يُعاد الرد من الذاكرة مباشرة ويُحدَّث بالخلفية إن قدُم (لا يشمل أي طلب خاص بالمستخدم).
const CACHEABLE_GET = /^\/?(departments|categories|banners|occasion-pages|products(\/offers)?(\?|$)|ai\/designs\/gallery|ai\/addon-fees|social-links)/;
const _swr = new Map(); // key -> {t, resp}
const SWR_TTL = 90000;      // صلاحية قصوى 90 ثانية
const SWR_FRESH = 15000;    // أقدم من 15 ثانية → تحديث بالخلفية
const _baseAdapter = axios.getAdapter(axios.defaults.adapter);
http.defaults.adapter = async (config) => {
  const isGet = (config.method || "get").toLowerCase() === "get";
  // fresh=1 يعني تجاوز كل الكاش — تستخدمه لوحة التحكم بعد الحفظ لقراءة أحدث البيانات
  const cacheable = isGet && CACHEABLE_GET.test(config.url || "")
    && !/[?&]fresh=/.test(config.url || "");
  const key = cacheable ? `${config.url}|${JSON.stringify(config.params || {})}` : "";
  if (cacheable) {
    const hit = _swr.get(key);
    if (hit && Date.now() - hit.t < SWR_TTL) {
      if (Date.now() - hit.t > SWR_FRESH) {
        _baseAdapter(config)
          .then((r) => { if (r.status === 200) _swr.set(key, { t: Date.now(), resp: r }); })
          .catch(() => {});
      }
      return { ...hit.resp, config };
    }
  }
  const resp = await _baseAdapter(config);
  if (cacheable && resp.status === 200) _swr.set(key, { t: Date.now(), resp });
  return resp;
};

http.interceptors.request.use((config) => {
  // رفع ملفات (FormData) يحتاج مهلة أطول من الافتراضية
  if (typeof FormData !== "undefined" && config.data instanceof FormData
      && (!config.timeout || config.timeout === 60000)) {
    config.timeout = 300000;
  }
  // 1. In-memory Bearer (works cross-origin, doesn't survive hard refresh)
  if (_memToken) config.headers.Authorization = `Bearer ${_memToken}`;
  try { config.headers["X-Lang"] = localStorage.getItem("rg_lang") || "ar"; } catch (_) {}
  // 🇸🇦 دولة المتجر الحالية — الباكند يستخدمها للتسعير السعودي الخاص
  try { if (!config.headers["X-RG-Country"]) config.headers["X-RG-Country"] = localStorage.getItem("rg_country") || "QA"; } catch (_) {}
  // 🌍 فرع المتجر الحالي — الباكند يستخدمه لفلترة ظهور الأقسام/التصنيفات/المنتجات
  try { const _rb = localStorage.getItem("rg_branch_id"); if (_rb) config.headers["X-RG-Branch"] = _rb; } catch (_) {}
  // 2. View-as override: super admin only (server-enforced).
  try {
    const raw = localStorage.getItem("rg_view_as");
    if (raw && config.url && config.url.includes("/admin")) {
      const v = JSON.parse(raw);
      config.params = config.params || {};
      if (v?.type === "country" && v.value) config.params.as_country = v.value;
      else if (v?.type === "branch" && v.value) config.params.as_branch_id = v.value;
    }
  } catch (_) { /* ignore */ }
  return config;
});

export default http;
